import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { onboardUser } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { useUserPhone } from "../lib/useIdentity";
import { Phone, Loader2 } from "lucide-react";
import { MaliStripe } from "./MaliStripe";
import { useT } from "../lib/lang";

/**
 * Blocking onboarding modal shown at first launch until the citizen
 * provides their phone number. No SMS/WhatsApp code is required —
 * the phone number alone is used to identify the citizen's own
 * alerts and contacts.
 *
 * Bypassed on /admin/* and /agence/* routes so operators can log in
 * directly, and on /share/* so public alert links remain accessible.
 */
export const OnboardingGate = ({ children }) => {
  const [t] = useT();
  const [storedPhone, setStoredPhone] = useUserPhone();
  const location = useLocation();

  // step: "phone" | "verified"
  const [step, setStep] = useState(storedPhone ? "verified" : "phone");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isExempt =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/agence/") ||
    location.pathname.startsWith("/share/");

  const bootRef = useRef(false);
  useEffect(() => {
    if (bootRef.current || isExempt) return;
    bootRef.current = true;
    setStep(storedPhone ? "verified" : "phone");
    // Security fix migration (Sep 2026): citizens onboarded BEFORE the
    // device-token fix have a phone in localStorage but no token, which
    // now 401s on contacts/alerts/medical-profile/notifications. Silently
    // re-run onboarding for the same phone to mint one — no prompt, no
    // SMS, the user never sees this happen.
    if (storedPhone && !userIdentity.getToken()) {
      const heal = () => {
        const p = onboardUser(storedPhone).catch((err) => {
          console.warn("device-token self-heal failed, will retry in 5s:", err?.message);
          setTimeout(() => {
            if (!userIdentity.getToken() && document.visibilityState === "visible") heal();
          }, 5000);
        });
        userIdentity.setHealPromise(p);
        return p;
      };
      heal();
    }
  }, [storedPhone, isExempt]);

  if (isExempt || step === "verified") return <>{children}</>;

  const cleanPhone = (raw) => (raw || "").replace(/\s+/g, "");

  const onSubmitPhone = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = cleanPhone(phoneDraft);
    if (cleaned.length < 4) {
      setError(t.onboardingShort || "Numéro trop court.");
      return;
    }
    setSubmitting(true);
    try {
      await onboardUser(cleaned);
      userIdentity.setPhone(cleaned);
      setStoredPhone(cleaned);
      setStep("verified");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || t.onboardingFailed || "Envoi impossible. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-rescue-bg flex items-stretch justify-center" data-testid="onboarding-gate">
      <div className="max-w-md w-full bg-white shadow-2xl flex flex-col">
        <MaliStripe thick />
        <div className="bg-rescue-ink text-white px-6 pb-8 pt-[calc(2rem+env(safe-area-inset-top))]">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-90 text-mali-yellow">{t.appName}</div>
          <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-2">
            {t.onboardingTitle || "Bienvenue"}
          </h1>
          <p className="text-sm leading-relaxed mt-3 opacity-95">
            {t.onboardingIntro || "Renseignez votre numéro de téléphone pour envoyer des alertes."}
          </p>
        </div>

        <form onSubmit={onSubmitPhone} className="px-6 py-6 space-y-4 flex-1">
          <div>
            <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> {t.onboardingLabel || "Votre numéro de téléphone"}
            </label>
            <input
              autoFocus
              data-testid="onboarding-phone-input"
              value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)}
              placeholder="+22370000000"
              inputMode="tel"
              className="mt-2 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-3 text-sm font-mono"
            />
          </div>

          {error && (
            <div className="text-xs text-rescue-red font-bold uppercase tracking-[0.15em]" data-testid="onboarding-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="onboarding-submit"
            disabled={submitting}
            className="w-full bg-rescue-ink text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-red transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</>
            ) : (
              <>Continuer</>
            )}
          </button>
        </form>

        <div className="px-6 pt-4 border-t-2 border-rescue-line text-[10px] text-rescue-mute leading-relaxed pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {t.onboardingFooter || "Ce numéro identifie vos propres alertes. Il ne sera jamais partagé."}
        </div>
      </div>
    </div>
  );
};
